'use client';

import React, { forwardRef, useRef } from 'react';
import { FaArrowLeft } from 'react-icons/fa6';
import { RxPerson } from 'react-icons/rx';

import Button from '@/components/@base/button';
import { BUTTON_TYPE } from '@/components/@base/button/type';
import Container from '@/components/@base/container';
import { Textarea } from '@/components/@base/input';
import { Heading } from '@/components/@base/typography';
import { HEADING_TYPE } from '@/components/@base/typography/heading/type';
import XImage from '@/components/@base/x-image';
import Props from '@/components/app/product/components/questions/add-question/type';
import useAddQuestion from '@/components/app/product/helpers/hooks';
import RoundedIcon from '@/components/shared/components/icon/rounded';

import {
  DATA_CY_ADD_QUESTION_BOX,
  DATA_CY_ADD_QUESTION_BOX_AFTER_LOGIN,
  DATA_CY_ADD_QUESTION_TEXTAREA,
  DATA_CY_SUBMIT_QUESTION_BUTTON,
} from '@/constant/cypress';
import ICON_SIZE from '@/constant/icon-size-color';
import imagesList from '@/constant/images-list';
import { pageLevelLocalization } from '@/constant/localization';

const { product: productLocalization } = pageLevelLocalization;

const AddQuestionBox = forwardRef<HTMLDivElement | HTMLElement, Props>(
  (
    { user, productId, slug, onUpdateQuestionList, parentId, focusQuestionBox = false },
    ref,
  ) => {
    const formRef = useRef<HTMLFormElement>(null);

    const { addComment, isLoading } = useAddQuestion({
      slug,
      productId,
      parentId,
      onUpdateQuestionList,
      formRef: formRef as React.RefObject<HTMLFormElement>,
    });

    const handlePostComment = (formData: FormData) => {
      const comment = formData.get('comment')?.toString();
      if (comment && comment.length > 0) {
        addComment(comment);
      }
    };

    return (
      <Container data-cy={DATA_CY_ADD_QUESTION_BOX_AFTER_LOGIN} id='question-box' ref={ref}>
        <Container center className='my-[30px] justify-start'>
          <RoundedIcon
            icon={
              <XImage
                src={imagesList.icons.question}
                alt={productLocalization.questions}
                width={20}
                height={20}
              />
            }
            className='z-10'
            iconClassName='bg-[#81CF33] bg-none'
          />
          <Container
            center
            className='-mr-4 h-11 w-11 rounded-full shadow-[0px_2px_8px_0px_#00000017]'
          >
            <Container center className='h-10 w-10 rounded-full bg-[#f9fafc]'>
              {user.avatar ? (
                <XImage
                  src={user.avatar.publicLink}
                  alt={user.publicName}
                  className='rounded-full'
                  width={40}
                  height={40}
                />
              ) : (
                <RxPerson size={ICON_SIZE.md} className='text-[#878F9B]' />
              )}
            </Container>
          </Container>

          <Heading type={HEADING_TYPE.H2} className='pr-4 text-lg font-bold text-[#424244]'>
            {productLocalization.addQuestion}
          </Heading>
        </Container>

        <form action={handlePostComment} ref={formRef}>
          <Textarea
            data-cy={DATA_CY_ADD_QUESTION_TEXTAREA}
            autoFocus={focusQuestionBox}
            placeholder={productLocalization.addYourQuestion}
            className='min-h-[163px] w-full rounded-md border-[1px] border-[#E5E8EB] p-3 text-[14px] font-light focus:outline-hidden'
            name='comment'
          ></Textarea>
          <Container center className='mt-3 justify-end'>
            <Button
              data-cy={DATA_CY_SUBMIT_QUESTION_BUTTON}
              Icon={<FaArrowLeft />}
              className='bg-[#81CF33] transition-all hover:bg-[#F5FBED] hover:text-[#81CF33]'
              type={BUTTON_TYPE.SUBMIT}
              isLoading={isLoading}
            >
              {productLocalization.saveQuestion}
            </Button>
          </Container>
        </form>
      </Container>
    );
  },
);

export default AddQuestionBox;
